import { Injectable } from "@angular/core";
import { ReceiptDto } from "../interfaces/user.interface";

@Injectable({
  providedIn: "root",
})
export class PdfGeneratorService {
  constructor() {}

  /** Converts image URL (assets/logo.jpg) to Base64 */
  private getBase64ImageFromURL(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = url;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL("image/png");
        resolve(dataURL);
      };
      img.onerror = (error) => reject(error);
    });
  }

  async generateReceiptPDF(
    printData: ReceiptDto[],
    isPrintRecentRecordOnly: boolean = false,
    customerName?: string
  ) {
    // Dynamically import pdfmake and fonts
    const pdfMakeModule = await import("pdfmake/build/pdfmake");
    const pdfFontsModule = await import("pdfmake/build/vfs_fonts");

    const pdfMake: any = (pdfMakeModule as any).default || pdfMakeModule;
    const pdfFonts: any = (pdfFontsModule as any).default || pdfFontsModule;

    pdfMake.vfs =
      pdfFonts?.pdfMake?.vfs || pdfFonts?.vfs || (pdfFontsModule as any).vfs;

    // Convert logo to Base64
    const logoBase64 = await this.getBase64ImageFromURL("assets/logo.jpg");

    // ✅ Use only the last record if flag is true
    const dataToPrint = isPrintRecentRecordOnly
      ? [printData[printData.length - 1]]
      : printData;

    const tableBody = [
      [
        { text: "Receipt Date", bold: true },
        { text: "Plan Name", bold: true },
        { text: "Receipt Number", bold: true },
        { text: "Installment Amount", bold: true },
        { text: "Rate", bold: true },
        { text: "Weight", bold: true },
      ],
      ...dataToPrint.map((d) => [
        new Date(d.receiptDate).toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
        d.planName == "A"
          ? "AKSHAYAM"
          : d.planName == "AV"
          ? "AKSHAYA VIRUKSHAM"
          : d.planName,
        d.receiptNumber,
        d.installmentAmount.toFixed(2),
        d.rate.toFixed(2),
        d.weight.toFixed(3),
      ]),
    ];

    const docDefinition: any = {
      content: [
        {
          image: logoBase64,
          width: 60,
          alignment: "center",
          margin: [0, 0, 0, 10],
        },
        {
          text: "Sri Vigneshwara Jewellery",
          style: "header",
          alignment: "center",
        },
        {
          text: `109, Bazaar Street, Pollachi\ncontactus@srivigneshwarajewellery.com\n+91 94893 47275`,
          style: "subheader",
          alignment: "center",
          margin: [0, 0, 0, 20],
        },
        { text: "Receipt Details", style: "sectionHeader" },
        {
          table: {
            headerRows: 1,
            widths: ["auto", "*", "auto", "auto", "auto", "auto"],
            body: tableBody,
          },
          layout: "lightHorizontalLines",
        },
      ],
      styles: {
        header: { fontSize: 18, bold: true },
        subheader: { fontSize: 10 },
        sectionHeader: { fontSize: 14, bold: true, margin: [0, 10, 0, 5] },
      },
    };

    const receiptNo =
      dataToPrint.length > 0 &&
      dataToPrint[dataToPrint.length - 1].receiptNumber
        ? dataToPrint[dataToPrint.length - 1].receiptNumber
        : "Receipt";

    const safeCustomerName = customerName
      ? customerName.replace(/[^\w\s-]/g, "").trim()
      : "";

    const fileName = safeCustomerName
      ? `${safeCustomerName} - ${receiptNo}.pdf`
      : `${receiptNo}.pdf`;

    // pdfMake.createPdf(docDefinition).open();
    pdfMake.createPdf(docDefinition).download(fileName);
  }
}
